/**
 * [CCI-15904] Helpers for the Post Stream Review toolbar's "View changes" button.
 *
 * The button drives `editor-plugin-show-diff`'s `showDiff` command, which needs:
 *   - `originalDoc`: a ProseMirror `Node` to be treated as the "left side" of
 *     the diff (the pre-edit doc).
 *   - `steps`: a `Step[]` describing how to turn `originalDoc` into the
 *     post-edit doc — i.e. forward (`original → live`) steps, the standard
 *     non-inverted contract.
 *
 * ── Algorithm ────────────────────────────────────────────────────────────────
 * The naive forward approach (compute `originalDoc → liveDoc` via a JSON diff
 * helper) is expensive — it has to round-trip both docs through JSON and run a
 * tree-diff over them.
 *
 * We avoid all of that by reconstructing the **pre-edit doc** for a single
 * segment from the data we already store:
 *
 *   1. Take the live `editorView.state.doc`.
 *   2. Replace `[startPos, endPos]` with the stored `originalUserContentSlice`
 *      — this throwaway transform reproduces what the doc looked like
 *      immediately before the AI change. That's our `originalDoc`.
 *   3. From a fresh `EditorState` over `originalDoc`, build a second throwaway
 *      transform that re-applies `segment.newSlice` over the mapped range.
 *      Its `tr.steps` ARE the forward `original → live` steps for this segment.
 *
 * Both transforms are pure / never dispatched. The returned shape
 *   `{ originalDoc, steps }`
 * matches `showDiff`'s native non-inverted contract: the caller passes
 * `isInverted: false` and `showDiff` walks the steps forward to render the
 * diff overlay.
 *
 * Pros:
 *   - Zero JSON serialisation; zero tree-diff cost.
 *   - Steps come straight from ProseMirror's `Transform`, so positions are
 *     guaranteed to be valid against `originalDoc`.
 *   - Pure schema-aware Step API — no extra dependencies (`@atlassian/content-reconciliation`
 *     is no longer needed for the diff path).
 *
 * The helper is pure: it never dispatches a transaction, never mutates editor
 * state, and never opens / closes the modal. The caller is responsible for
 * forwarding the returned `{ originalDoc, steps }` to
 * `api.showDiff.commands.showDiff(...)` with `isInverted: false`.
 */
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { Step as ProseMirrorStep } from '@atlaskit/editor-prosemirror/transform';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIContentPositionEntry } from '../../../../pm-plugins/ai-session-context/types';

/**
 * Build the `{ originalDoc, forward steps }` pair needed by `showDiff` for a
 * single Post Stream Review segment.
 *
 * Returns `null` when the segment can't be safely diffed (no stored original
 * slice, or positions are no longer valid against the live doc, or the schema
 * rejects either of the two throwaway replaces).
 *
 * Algorithm:
 *   1. Take the live `editorView.state.doc`.
 *   2. Build throwaway transform A: replace `[segment.startPos, segment.endPos]`
 *      with `segment.originalUserContentSlice`. The result `tr.doc` is the
 *      reconstructed pre-edit doc — this is our `originalDoc`. Use mapping
 *      bias `-1` on the end position so it anchors at the end of the replaced
 *      range (matches the convention in `ai-session-context-plugin.ts`).
 *   3. Build throwaway transform B over a fresh `EditorState({ doc: originalDoc })`
 *      that replaces the mapped `[newStartPos, newEndPos]` with
 *      `segment.newSlice`. Its `tr.steps` are forward `original → live` steps.
 *   4. Return `{ originalDoc, steps }`. The caller passes `isInverted: false`.
 *
 * Why this is correct even after undos / collaborator edits:
 *   - The live doc is always read fresh (`state.tr` uses `state.doc`).
 *   - `segment.startPos` / `segment.endPos` are continuously remapped by the
 *     `ai-session-context` plugin's `apply()` method.
 *   - `segment.originalUserContentSlice` and `segment.newSlice` are immutable.
 *
 * The caller MUST disable the button when the segment is undone (so the live
 * doc already shows the original content) — diffing in that case would yield
 * empty steps, which is wasted work AND would render an empty diff overlay.
 */
export const buildDiffParamsForSegment = (
	editorView: EditorView,
	segment: AIContentPositionEntry,
): { originalDoc: PMNode; steps: ProseMirrorStep[] } | null => {
	const { state } = editorView;
	const { doc } = state;

	if (
		!segment?.originalUserContentSlice ||
		!segment.newSlice ||
		// `== null` (not `!`): `startPos === 0` is a valid PM document position
		// (the very start of the doc). Using `!startPos` would silently disable
		// View Changes for any segment whose content begins at position 0.
		segment.startPos == null ||
		segment.endPos == null
	) {
		return null;
	}

	// Bounds check: `tr.replaceRange` would throw on out-of-bounds positions.
	if (
		segment.startPos < 0 ||
		segment.endPos < segment.startPos ||
		segment.endPos > doc.content.size
	) {
		return null;
	}

	// Build the inverse transform as a *throwaway* — we never dispatch it.
	// Using `state.tr` is the canonical way to get a Transform pre-configured
	// with the right schema + doc. The resulting `tr.steps` are exactly the
	// step list `showDiff` wants when invoked with `isInverted: true`.
	try {
		const tr = state.tr;
		tr.replaceRange(segment.startPos, segment.endPos, segment.originalUserContentSlice);
		const originalDoc = tr.doc;

		// Choose mapping biases so that, after the original content has
		// been inserted, `[newStartPos, newEndPos]` BRACKETS that inserted content.
		//
		// For a non-empty live range (update/add) the default biases work:
		//   - startPos with assoc +1 lands at the start of the inserted content,
		//   - endPos with assoc -1 lands at its end.
		// For a ZERO-WIDTH live range (a REMOVE — `startPos === endPos`, e.g. a
		// removed node) the two map calls operate on the SAME point, so the default
		// biases collapse the wrong way: startPos (+1) jumps to AFTER the inserted
		// original and endPos (-1) stays BEFORE it, yielding an inverted/empty range
		// and NO visible diff. Flip the biases in that case (start: -1, end: +1) so
		// the range correctly spans the re-inserted removed content.
		const isZeroWidthRange = segment.startPos === segment.endPos;
		const newStartPos = tr.mapping.map(segment.startPos, isZeroWidthRange ? -1 : 1);
		const newEndPos = tr.mapping.map(segment.endPos, isZeroWidthRange ? 1 : -1);
		const newState = EditorState.create({ doc: originalDoc });
		const steps = newState.tr.replaceRange(newStartPos, newEndPos, segment.newSlice).steps;

		return { originalDoc, steps };
	} catch {
		// Most likely cause: schema rejected the original slice because the
		// surrounding context has changed in a way that no longer accepts it.
		// We bail out — the caller will leave the button as a no-op.
		// TODO: CCI-15904 - fire a `viewChangesDiffFailed` analytics event from
		// the caller so we can monitor real-world breakage rates.
	}
	return null;
};

/**
 * Build the `{ originalDoc, forward steps }` pair needed by
 * `showDiff` for the AGGREGATE "all changes" view — i.e. every AI segment at
 * once, used when the Post Stream Review modal first opens.
 *
 * Same idea as {@link buildDiffParamsForSegment} but for N entries:
 *   1. Reconstruct the full pre-edit doc by replacing EACH entry's
 *      `[startPos, endPos]` with its `originalUserContentSlice`. We apply the
 *      entries back-to-front (descending `startPos`) in a single throwaway
 *      transform so each replace doesn't invalidate the positions of the ones
 *      not yet processed. The result is our `originalDoc`.
 *   2. Over a fresh `EditorState({ doc: originalDoc })`, re-apply every entry's
 *      `newSlice` (front-to-back, mapping positions through the running
 *      transform). The accumulated `tr.steps` are the forward `original → live`
 *      steps for the whole change.
 *
 * Entries that are currently `isUndone` are skipped for BOTH passes: their live
 * range already shows the original content, so they contribute no diff.
 *
 * Returns `null` when there is nothing to diff (no applicable entries) or when a
 * replace throws (schema rejection / stale positions) — the caller then leaves
 * "View changes" as a no-op.
 *
 * The helper is pure: it never dispatches, mutates state, or opens the modal.
 */
export const buildDiffParamsForAllSegments = (
	editorView: EditorView,
	segments: AIContentPositionEntry[],
): { originalDoc: PMNode; steps: ProseMirrorStep[] } | null => {
	const { state } = editorView;
	const { doc } = state;

	const applicable = (segments ?? []).filter(
		(s) =>
			s &&
			!s.isUndone &&
			s.originalUserContentSlice &&
			s.newSlice &&
			s.startPos != null &&
			s.endPos != null &&
			s.startPos >= 0 &&
			s.endPos >= s.startPos &&
			s.endPos <= doc.content.size,
	);

	if (applicable.length === 0) {
		return null;
	}

	try {
		// Pass 1 — reconstruct the pre-edit doc. Apply originals back-to-front so
		// earlier replaces don't shift the positions of later (earlier-in-doc)
		// ones. We also remember, for each entry, the position its NEW content
		// should re-occupy, expressed against the live doc, so pass 2 can map it.
		const descending = [...applicable].sort((a, b) => b.startPos - a.startPos);
		const tr = state.tr;
		for (const seg of descending) {
			tr.replaceRange(seg.startPos, seg.endPos, seg.originalUserContentSlice);
		}
		const originalDoc = tr.doc;

		// Pass 2 — re-apply each new slice over the reconstructed doc, front to
		// back. Map each entry's original live `[startPos, endPos]` through pass
		// 1's mapping to find where its original content now sits in `originalDoc`,
		// then replace that with the new slice. Accumulate the forward steps.
		const ascending = [...applicable].sort((a, b) => a.startPos - b.startPos);
		const newState = EditorState.create({ doc: originalDoc });
		const forwardTr = newState.tr;
		for (const seg of ascending) {
			// Mirror the bias handling in `buildDiffParamsForSegment`: for
			// a ZERO-WIDTH live range (a REMOVE, `startPos === endPos`) the default
			// biases collapse the wrong way and yield an inverted/empty range — so the
			// removed content would show NO diff. Flip the biases (start: -1, end: +1)
			// in that case so the mapped range brackets the re-inserted original.
			const isZeroWidthRange = seg.startPos === seg.endPos;
			const from = forwardTr.mapping.map(
				tr.mapping.map(seg.startPos, isZeroWidthRange ? -1 : 1),
				isZeroWidthRange ? -1 : 1,
			);
			const to = forwardTr.mapping.map(
				tr.mapping.map(seg.endPos, isZeroWidthRange ? 1 : -1),
				isZeroWidthRange ? 1 : -1,
			);
			forwardTr.replaceRange(from, to, seg.newSlice);
		}

		return { originalDoc, steps: forwardTr.steps };
	} catch {
		// Schema rejection or stale positions — leave View changes as a no-op.
	}
	return null;
};
